import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { config } from '../../config/environment';
import { 
  Calculator, 
  Percent, 
  DollarSign, 
  RefreshCw, 
  Save, 
  AlertCircle,
  TrendingUp,
  Package,
  Truck,
  Tag
} from 'lucide-react';

const BACKEND_URL = config.backendUrl;

const CostingControlSheet = () => {
  const [settings, setSettings] = useState({
    labor_percent: 10,
    import_shipping_percent: 50,
    packaging_handling_percent: 25,
    shipping_percent: 0,
    marketing_percent: 15,
    wholesale_margin_target: 40,
    retail_margin_target: 60
  });
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/v1/settings/costing-control-sheet?tenant_id=tenant-123`);
      if (!response.ok) throw new Error('Failed to fetch settings');
      
      const data = await response.json();
      if (data.success && data.data) {
        setSettings(data.data);
        setOriginalSettings(data.data);
        setLastUpdated(data.updated_at);
      }
    } catch (error) {
      console.error('Error fetching costing settings:', error);
      toast.error('Failed to load costing settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Check for unsaved changes
  useEffect(() => {
    if (originalSettings) {
      const changed = Object.keys(settings).some(
        key => settings[key] !== originalSettings[key]
      );
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  // Handle input changes
  const handleChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    // Cost percentages (labor, import, fulfillment, marketing) can exceed 100%
    // Only margin targets should be clamped to 100%
    const isMarginField = field.includes('margin_target');
    const maxValue = isMarginField ? 100 : 999;
    setSettings(prev => ({
      ...prev,
      [field]: Math.min(maxValue, Math.max(0, numValue))
    }));
  };

  // Save settings
  const saveSettings = async (recalculate = false) => {
    try {
      setSaving(true);
      if (recalculate) setRecalculating(true);

      const response = await fetch(
        `${BACKEND_URL}/api/v1/settings/costing-control-sheet?tenant_id=tenant-123&recalculate=${recalculate}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings)
        }
      );

      if (!response.ok) throw new Error('Failed to save settings');

      const data = await response.json();
      
      if (data.success) {
        setOriginalSettings(settings);
        setHasChanges(false);
        setLastUpdated(new Date().toISOString());
        
        if (recalculate && data.recalculation) {
          toast.success(
            `Settings saved! Updated ${data.recalculation.raw_materials_updated} raw materials and ${data.recalculation.finished_products_updated} finished products.`
          );
        } else {
          toast.success('Settings saved successfully');
        }
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
      setRecalculating(false);
    }
  };

  // Recalculate raw materials only
  const recalculateRawMaterials = async () => {
    try {
      setRecalculating(true);
      toast.info('Recalculating raw materials...');
      
      const response = await fetch(
        `${BACKEND_URL}/api/v1/settings/recalculate-raw-materials?tenant_id=tenant-123`,
        { method: 'POST' }
      );
      
      if (!response.ok) throw new Error('Failed to recalculate');
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Updated ${data.raw_materials_updated} raw materials`);
      }
    } catch (error) {
      console.error('Error recalculating raw materials:', error);
      toast.error('Failed to recalculate raw materials');
    } finally {
      setRecalculating(false);
    }
  };

  // Recalculate all products (raw materials first, then finished products)
  const recalculateAllProducts = async () => {
    try {
      setRecalculating(true);
      toast.info('Recalculating all products... This may take a moment.');
      
      const response = await fetch(
        `${BACKEND_URL}/api/v1/settings/recalculate-all?tenant_id=tenant-123`,
        { method: 'POST' }
      );
      
      if (!response.ok) throw new Error('Failed to recalculate');
      
      const data = await response.json();
      if (data.success) {
        toast.success(
          `Updated ${data.raw_materials_updated} raw materials and ${data.finished_products_updated} finished products`
        );
      }
    } catch (error) {
      console.error('Error recalculating all products:', error);
      toast.error('Failed to recalculate products');
    } finally {
      setRecalculating(false);
    }
  };

  // Calculate example costs for preview with split fulfillment
  const calculateExample = () => {
    const rawCost = 0.70; // Example raw material cost
    const laborCost = rawCost * (settings.labor_percent / 100);
    const importCost = rawCost * (settings.import_shipping_percent / 100);
    const landedCost = rawCost + laborCost + importCost;
    
    // Finished product example (10 units of raw material)
    const componentCost = landedCost * 10;
    
    // Split fulfillment costs
    const packagingCost = componentCost * (settings.packaging_handling_percent / 100);
    const shippingCost = componentCost * (settings.shipping_percent / 100);
    const totalFulfillmentCost = packagingCost + shippingCost;
    
    // COGS based on who pays shipping
    const cogsCustomerPays = componentCost + packagingCost; // Customer pays shipping
    const cogsSellerPays = componentCost + packagingCost + shippingCost; // Seller pays shipping
    
    // Suggested prices for each scenario
    const wholesaleMargin = settings.wholesale_margin_target / 100;
    const retailMargin = settings.retail_margin_target / 100;
    
    // Customer pays shipping scenario
    const wholesalePriceCustomerPays = cogsCustomerPays / (1 - wholesaleMargin);
    const retailPriceCustomerPays = cogsCustomerPays / (1 - retailMargin);
    
    // Seller pays shipping scenario
    const wholesalePriceSellerPays = cogsSellerPays / (1 - wholesaleMargin);
    const retailPriceSellerPays = cogsSellerPays / (1 - retailMargin);
    
    return {
      rawCost,
      laborCost,
      importCost,
      landedCost,
      componentCost,
      packagingCost,
      shippingCost,
      totalFulfillmentCost,
      cogsCustomerPays,
      cogsSellerPays,
      wholesalePriceCustomerPays,
      retailPriceCustomerPays,
      wholesalePriceSellerPays,
      retailPriceSellerPays
    };
  };

  const example = calculateExample();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-400" />
            Costing Control Sheet
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Set company-wide cost markup percentages and target margins. These values auto-apply to all product cost calculations.
          </p>
        </div>
        {lastUpdated && (
          <span className="text-xs text-gray-500">
            Last updated: {new Date(lastUpdated).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="flex items-center gap-2 p-2 bg-yellow-900/30 border border-yellow-700 rounded-lg">
          <AlertCircle className="w-4 h-4 text-yellow-500" />
          <span className="text-xs text-yellow-400">You have unsaved changes</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cost Markup Percentages */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Package className="w-4 h-4 text-green-400" />
              Cost Markup Percentages
            </CardTitle>
            <CardDescription className="text-xs text-gray-400">
              Applied to raw material cost to calculate landed cost
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-4">
            {/* Labor % */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Labor %</span>
                <span className="text-gray-500">(applied to raw material cost)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  step="0.1"
                  value={settings.labor_percent}
                  onChange={(e) => handleChange('labor_percent', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="labor-percent-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>

            {/* Import/Shipping % */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Import/Shipping %</span>
                <span className="text-gray-500">(applied to raw material cost)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  step="0.1"
                  value={settings.import_shipping_percent}
                  onChange={(e) => handleChange('import_shipping_percent', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="import-shipping-percent-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fulfillment Costs (Split) */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Package className="w-4 h-4 text-green-400" />
              Fulfillment Costs
            </CardTitle>
            <CardDescription className="text-xs text-gray-400">
              Split into packaging and shipping for flexible pricing
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-3">
            {/* Packaging & Handling % */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Packaging &amp; Handling %</span>
                <span className="text-gray-500">(applied to component cost)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  step="0.1"
                  value={settings.packaging_handling_percent}
                  onChange={(e) => handleChange('packaging_handling_percent', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="packaging-handling-percent-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>

            {/* Shipping % */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Shipping %</span>
                <span className="text-gray-500">(applied to component cost)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  step="0.1"
                  value={settings.shipping_percent}
                  onChange={(e) => handleChange('shipping_percent', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="shipping-percent-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>

            <div className="flex items-start gap-2 p-2 bg-blue-900/20 rounded-lg border border-blue-700/50">
              <AlertCircle className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-gray-400">
                <span className="text-green-400">Packaging</span> applies to all orders.{' '}
                <span className="text-orange-400">Shipping</span> only applies when seller pays shipping.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Marketing Cost */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-pink-400" />
              Marketing Cost
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-3">
            {/* Marketing % */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Marketing %</span>
                <span className="text-gray-500">(% of revenue at sale)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  step="0.1"
                  value={settings.marketing_percent}
                  onChange={(e) => handleChange('marketing_percent', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="marketing-percent-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Target Margins */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              Target Profit Margins
            </CardTitle>
            <CardDescription className="text-xs text-gray-400">
              Used to calculate suggested selling prices
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-4">
            {/* Wholesale Margin Target */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Wholesale Margin Target</span>
                <span className="text-gray-500">(for B2B pricing)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.wholesale_margin_target}
                  onChange={(e) => handleChange('wholesale_margin_target', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="wholesale-margin-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>

            {/* Retail Margin Target */}
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-300 flex items-center justify-between">
                <span>Retail Margin Target</span>
                <span className="text-gray-500">(for D2C pricing)</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.retail_margin_target}
                  onChange={(e) => handleChange('retail_margin_target', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white pr-8 h-9 text-sm"
                  data-testid="retail-margin-input"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>

            {/* Formula Explanation */}
            <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-400 font-medium mb-2">Price Calculation Formula:</p>
              <code className="text-xs text-blue-400 block">
                Suggested Price = Total COGS ÷ (1 - Target Margin%)
              </code>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Preview */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <Tag className="w-4 h-4 text-orange-400" />
            Live Preview (Example Calculation)
          </CardTitle>
          <CardDescription className="text-xs text-gray-400">
            See how your settings affect product costs in real-time
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Raw Material Example */}
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <h4 className="text-xs font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <Package className="w-3.5 h-3.5" />
                Raw Material → Landed Cost
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Raw Material Cost:</span>
                  <span className="text-white font-mono">${(Number(example.rawCost) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">+ Labor ({settings.labor_percent}%):</span>
                  <span className="text-green-400 font-mono">+${(Number(example.laborCost) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">+ Import/Shipping ({settings.import_shipping_percent}%):</span>
                  <span className="text-green-400 font-mono">+${(Number(example.importCost) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-gray-600 pt-1.5 mt-1.5">
                  <span className="text-white font-medium">Landed Cost:</span>
                  <span className="text-blue-400 font-mono font-semibold">${(Number(example.landedCost) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Finished Product Example */}
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <h4 className="text-xs font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <Truck className="w-3.5 h-3.5" />
                Finished Product (10 units) → Costs
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Component Cost (10 × ${(Number(example.landedCost) || 0).toFixed(2)}):</span>
                  <span className="text-white font-mono">${(Number(example.componentCost) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">+ Packaging &amp; Handling ({settings.packaging_handling_percent}%):</span>
                  <span className="text-green-400 font-mono">+${(Number(example.packagingCost) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">+ Shipping ({settings.shipping_percent}%):</span>
                  <span className="text-orange-400 font-mono">+${(Number(example.shippingCost) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Scenarios */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Customer Pays Shipping */}
            <div className="p-3 bg-green-900/20 rounded-lg border border-green-700/50">
              <h4 className="text-xs font-semibold text-green-300 mb-2">
                IF CUSTOMER PAYS SHIPPING
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total COGS:</span>
                  <span className="text-blue-400 font-mono font-semibold">${(Number(example.cogsCustomerPays) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Wholesale ({settings.wholesale_margin_target}% margin):</span>
                  <span className="text-purple-400 font-mono">${(Number(example.wholesalePriceCustomerPays) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Retail ({settings.retail_margin_target}% margin):</span>
                  <span className="text-purple-400 font-mono">${(Number(example.retailPriceCustomerPays) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Seller Pays Shipping */}
            <div className="p-3 bg-orange-900/20 rounded-lg border border-orange-700/50">
              <h4 className="text-xs font-semibold text-orange-300 mb-2">
                IF SELLER PAYS SHIPPING
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total COGS:</span>
                  <span className="text-blue-400 font-mono font-semibold">${(Number(example.cogsSellerPays) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Wholesale ({settings.wholesale_margin_target}% margin):</span>
                  <span className="text-purple-400 font-mono">${(Number(example.wholesalePriceSellerPays) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Retail ({settings.retail_margin_target}% margin):</span>
                  <span className="text-purple-400 font-mono">${(Number(example.retailPriceSellerPays) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Recalculate Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-green-400" />
            Bulk Recalculation
          </CardTitle>
          <CardDescription className="text-xs text-gray-400">
            Recalculate product costs without changing settings
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <div className="space-y-3">
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-white">Raw Materials Only</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Recalculate landed costs for all raw materials using current percentages
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={recalculateRawMaterials}
                  disabled={recalculating}
                  className="text-xs h-8"
                  data-testid="recalculate-raw-materials-btn"
                >
                  {recalculating ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Package className="w-3.5 h-3.5 mr-1.5" />
                      Recalculate
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="p-3 bg-gray-700/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-white">All Products (Cascade)</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Step 1: Recalculate all raw material landed costs<br/>
                    Step 2: Recalculate all finished product BOMs and pricing
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={recalculateAllProducts}
                  disabled={recalculating}
                  className="text-xs h-8 border-green-600 text-green-400 hover:bg-green-600/20"
                  data-testid="recalculate-all-products-btn"
                >
                  {recalculating ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Calculator className="w-3.5 h-3.5 mr-1.5" />
                      Recalculate All
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-2 p-2 bg-blue-900/20 rounded-lg border border-blue-700/50">
              <AlertCircle className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-gray-400">
                Products in <span className="text-purple-400">manual mode</span> will be skipped. 
                Use the &quot;Save &amp; Recalculate All&quot; button to also save any settings changes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          variant="outline"
          onClick={fetchSettings}
          disabled={saving || loading}
          className="text-xs h-9"
          data-testid="reset-settings-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Reset
        </Button>
        <Button
          variant="outline"
          onClick={() => saveSettings(false)}
          disabled={saving || !hasChanges}
          className="text-xs h-9"
          data-testid="save-settings-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          Save Only
        </Button>
        <Button
          onClick={() => saveSettings(true)}
          disabled={saving}
          className="text-xs h-9 bg-blue-600 hover:bg-blue-700"
          data-testid="save-and-recalculate-btn"
        >
          {recalculating ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              Recalculating...
            </>
          ) : (
            <>
              <Calculator className="w-3.5 h-3.5 mr-1.5" />
              Save & Recalculate All
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default CostingControlSheet;
